import { useQuery } from '@tanstack/react-query'
import { queryClient } from './queryClient.js'
import { config as configApi } from './api.js'

/**
 * The config bundle: one object keyed by filename stem, stamped with
 * config_version (config CONTRACT.md s0).
 *
 * Config is a build artifact, not a runtime resource. The client fetches the
 * bundle, caches it by version, and renders from it. Nothing here edits it.
 */

export const CONFIG_KEY = ['config']
/*
 * The app is Editrics. Renamed outright, with NO migration and no dual-prefix
 * cleanup: operator's ruling, there are no real users and nothing in anyone's
 * localStorage is worth preserving. A dev with a stale key pays one hard
 * refresh, which is cheaper than carrying a compatibility path forever for a
 * value that is a cache and can always be refetched.
 */
const STORAGE_KEY = 'editrics.config'

function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && parsed.config_version ? parsed : null
  } catch {
    return null // a corrupt or unavailable cache is just a cache miss
  }
}

function writeCache(bundle) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bundle))
  } catch {
    // private mode or a full quota. The app works without the cache.
  }
}

async function fetchBundle() {
  const bundle = await configApi.bundle()
  writeCache(bundle)
  return bundle
}

/**
 * Cached config paints immediately; the server is still asked on every mount, so
 * a redeployed bundle is picked up without the participant doing anything.
 * This caches CONFIG only. Answers are never cached client-side — they are
 * saved per item, and resume is a query.
 */
export function useConfigBundle() {
  return useQuery({
    queryKey: CONFIG_KEY,
    queryFn: fetchBundle,
    initialData: () => readCache() ?? undefined,
    initialDataUpdatedAt: 0, // the cache is always stale, so it revalidates
    staleTime: 5 * 60 * 1000,
    gcTime: Infinity,
  })
}

/**
 * Forget everything cached on this device.
 *
 * queryClient.clear() only empties memory; the config bundle also sits in
 * localStorage, where it survived both sign-out and withdrawal. Someone holding
 * the phone afterwards could read it from the storage inspector — not a glance
 * and not the back button, but a real read on a device that may not be solely
 * the participant's.
 *
 * Withdrawal is the case that matters: a withdrawn participant is 403'd out and
 * can never return, so whatever is left behind is left behind permanently.
 *
 * Costs nothing, which is why it is simply done rather than weighed. The cache
 * is a paint optimisation: answers are never cached and resume is a server
 * query, so nothing here is load-bearing.
 *
 * Scoped to this app's own keys rather than localStorage.clear(), so it cannot
 * take out something it does not own; the prefix covers anything added later.
 */
export function clearCachedConfig() {
  try {
    const ours = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (key === STORAGE_KEY || key?.startsWith('editrics.')) ours.push(key)
    }
    ours.forEach((key) => localStorage.removeItem(key))
  } catch {
    // private mode, or storage unavailable. Nothing was cached either.
  }
}

/** Imperative read, for code outside React. */
export function currentBundle() {
  return queryClient.getQueryData(CONFIG_KEY) ?? readCache()
}

/** Imperative refetch, used when the server says our version is stale. */
export async function refreshConfig() {
  return queryClient.fetchQuery({ queryKey: CONFIG_KEY, queryFn: fetchBundle, staleTime: 0 })
}

/* ---------------------------------------------------------------- *
 * Selectors. The bundle is keyed by filename stem, and each value is
 * the whole file object, so the inner key is named too.
 * ---------------------------------------------------------------- */

export const formatsOf = (bundle) => bundle?.response_formats?.formats ?? {}
export const day0ItemsOf = (bundle) => bundle?.day0_items?.items ?? []
export const emaItemsOf = (bundle) => bundle?.ema_items?.items ?? []
export const demographicsFieldsOf = (bundle) => bundle?.demographics?.fields ?? []
export const crisisResourcesOf = (bundle) => bundle?.crisis_resources ?? null

/**
 * The country list is NOT derived from config and deliberately not from
 * crisis_resources.countries — that holds only the four countries with resource
 * entries, so using it would make roughly 245 countries unregisterable. See
 * platformLists.js: ISO 3166-1 alpha-2 comes from the platform.
 */

export const consentOf = (bundle) => bundle?.consent?.consent ?? bundle?.consent ?? null

/**
 * Screen copy (CFG-30) — the participant-facing sentences that are not
 * instrument text, consent, crisis or notification copy. Until this file
 * existed these lived as literals in frontend/ and backend/, governed by
 * nothing and swept by nothing.
 *
 * design owns which facts appear and in what order; config owns the wording.
 */
export const screensOf = (bundle) => bundle?.screens?.screens ?? null

export function findItem(bundle, itemId) {
  return (
    day0ItemsOf(bundle).find((i) => i.item_id === itemId) ??
    emaItemsOf(bundle).find((i) => i.item_id === itemId) ??
    null
  )
}

/**
 * Everything about a format that changes what the participant sees. Provenance
 * keys (note, source, TODO) are deliberately excluded: a comment edit must not
 * count as the item having changed under someone.
 */
export function renderSignature(format) {
  if (!format) return null
  const { kind, options, min, max, step, unit, zero_means, free_text, free_text_required } = format
  return JSON.stringify({
    kind,
    options,
    min,
    max,
    step,
    unit,
    zero_means,
    free_text,
    free_text_required,
  })
}
